import { cartesianProduct } from '../../../utils/array/cartesian.utils';

/**
 * @fileoverview Flat attribute-value structure + transform functions
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  STRUCTURE                                                       │
 * │                                                                  │
 * │  [                                                               │
 * │    { value: "Màu sắc",   title: "Đỏ",   icon: "blob://red",      │
 * │      after_price: 1000, quantity: 10,   discount: 0    },        │
 * │    { value: "Màu sắc",   title: "Xanh", icon: "blob://blue",     │
 * │      after_price: 1000, quantity: 5,    discount: 100  },        │
 * │    { value: "Kích thước", title: "S",   icon: null,               │
 * │      after_price: 0,    quantity: 0,    discount: 0    },        │
 * │    { value: "Kích thước", title: "M",   icon: null,               │
 * │      after_price: 0,    quantity: 0,    discount: 0    },        │
 * │  ]                                                               │
 * │                                                                  │
 * │  Mỗi item = 1 attribute value (Đỏ, Xanh, S, M...)               │
 * │  Group bằng "value" để biết thuộc attr nào                        │
 * └──────────────────────────────────────────────────────────────────┘
 */

// ─── JSDoc Types ──────────────────────────────────────────────────────────────

/**
 * @typedef {Object} AttributeValue
 * @property {string}      value       - Tên thuộc tính, vd "Màu sắc"
 * @property {string}      title       - Tên giá trị, vd "Đỏ"
 * @property {string|null} icon        - URL ảnh (blob hoặc server URL)
 * @property {number}      after_price - Giá sau (giá bán)
 * @property {number}      quantity    - Tồn kho
 * @property {number}      discount    - Giảm giá
 */

/**
 * @typedef {Object} TableRow
 * @property {string}           id        - Composite key, vd "0-1" (typeIdx-valueIdx)
 * @property {AttributeValue[]} attrs     - Full list entries trong combination này
 * @property {string}           attr1     - title của attr đầu tiên (group/rowspan)
 * @property {string}           attr1Sub  - value của attr đầu tiên
 * @property {string|null}      imageUrl  - icon của attr đầu tiên
 * @property {string}           attr2     - title của attr thứ hai (nếu có)
 * @property {string}           attr2Sub  - value của attr thứ hai
 * @property {number}           qty
 * @property {number}           price
 * @property {number}           discount
 * @property {boolean}          isDefault
 */

// ─── JSDoc: VariantCombination ────────────────────────────────────────────────

/**
 * @typedef {Object} VariantCombination
 * @property {string}                  title     - Value titles nối bằng " | ", vd "Đỏ | S"
 * @property {Object.<string,string>}  type      - Position → tên attr group. Vd: { variant1: "Màu sắc", variant2: "Kích thước" }
 * @property {*}                       icon      - variantIcons[attrId][valueId][0] của attr đầu tiên; null nếu không có
 * @property {number}                  price     - Giá bán, default 0
 * @property {number}                  quantity  - Tồn kho, default 0
 * @property {number}                  discount  - Giảm giá, default 0
 * @property {boolean}                 isDefault - true nếu comboKey === isDefault param
 */

// // ─── Helpers ──────────────────────────────────────────────────────────────────

// /**
//  * Group array by key, preserving first-seen insertion order.
//  * Map iteration order = attribute order from input.
//  * @template T
//  * @param {T[]} arr
//  * @param {(item: T) => string} keyFn
//  * @returns {Map<string, T[]>}
//  */
// function groupBy(arr, keyFn) {
//   return arr.reduce((map, item) => {
//     const k = keyFn(item);
//     if (!map.has(k)) map.set(k, []);
//     map.get(k).push(item);
//     return map;
//   }, new Map());
// }

/**
 * Parse value to non-negative number, fallback 0 on invalid/empty input.
 * Strips thousand separators (,) before parsing (VN format "1,000,000" → 1000000).
 * @param {any} val
 * @returns {number}
 */
function toNumber(val) {
  if (val === null || val === undefined || val === '') return 0;
  const n = Number(String(val).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
}

/**
 * Build composite key from combination's valueIds.
 * Single attr: key = valueId (e.g. "1").
 * Multi attr: key = "v1Id-v2Id-..." (e.g. "1-2").
 * Key must match form state to retrieve qty/price/discount.
 * @param {Array<{ valueId: string }>} combo
 * @param {boolean} isSingle
 * @returns {string}
 */
function buildComboKey(combo, isSingle) {
  return isSingle
    ? String(combo[0].valueId)
    : combo.map(c => c.valueId).join('-');
}

/**
 * Build type map: { variant1: attrName1, variant2: attrName2, ... }
 * Position (variant1, variant2) = order in combination (left→right).
 * @param {Array<{ attrName: string }>} combo
 * @returns {Object.<string, string>}
 */
function buildTypeMap(combo) {
  return combo.reduce((acc, c, i) => {
    acc[`variant${i + 1}`] = c;
    return acc;
  }, {});
}

// ─── Convert: form state → VariantCombination[] ───────────────────────────────

/**
 * Convert form state (from product inventory page) → VariantCombination[].
 * Generate all possible combinations via Cartesian product.
 *
 * Key convention (must match form state keys for qty/price/discount lookup):
 *   Single attr: "valueId"               (e.g. "1", "2")
 *   Multi attr:  "valueId1-valueId2-..." (e.g. "1-1", "1-2", "2-1")
 *
 * @param {Object} params
 * @param {Record<string, string>}                  params.variantNames     - attrId → attribute name
 * @param {Record<string, Record<string, string>>}  params.variantValues    - attrId → (valueId → title)
 * @param {Record<string, Record<string, Array>>}   [params.variantIcons]   - attrId → (valueId → [iconUrl])
 * @param {Record<string, Record<string, Array>>}   [params.variantImages]  - "${attrId}-${valueId}" → [imageUrl]
 * @param {Record<string, string>}                  [params.quantity]       - key → qty (form value, may be string)
 * @param {Record<string, string>}                  [params.price]          - key → price (form value, may be string "1,000")
 * @param {Record<string, string|undefined>}        [params.discount]       - key → discount
 * @param {string|null}                             [params.isDefault]      - key of default variant
 * @returns {VariantCombination[]}
 *
 * @example
 * // Single attribute (color only)
 * fromFormState({
 *   variantNames:  { "1": "Màu sắc" },
 *   variantValues: { "1": { "1": "Đỏ", "2": "Xanh" } },
 *   quantity:      { "1": "10", "2": "5" },
 *   price:         { "1": "200,000", "2": "180,000" },
 *   isDefault:     "1"
 * })
 * // → [
 * //   { id: 0, title: "Đỏ", type: { variant1: "Màu sắc" },
 * //     price: 200000, quantity: 10, discount: 0, is_default: true },
 * //   { id: 1, title: "Xanh", type: { variant1: "Màu sắc" },
 * //     price: 180000, quantity: 5, discount: 0, is_default: false }
 * // ]
 */
export function fromFormState({
  variantNames = {},
  variantValues = {},
  variantIcons = {},
  variantImages = {},
  quantity = {},
  price = {},
  discount = {},
  isDefault = null,
} = {}) {
  const attrIds = Object.keys(variantNames).sort(
    (a, b) => Number(a) - Number(b)
  );

  // Convert each attribute's values into { attrId, valueId, attrName, value, icon, images }
  const attrArrays = attrIds
    .map(attrId =>
      Object.entries(variantValues[attrId] ?? {}).map(([valueId, value]) => {
        return {
          attrId,
          valueId,
          attrName: variantNames[attrId],
          value,
          icon: variantIcons?.[attrId]?.[valueId]?.[0] ?? null,
        };
      })
    )
    .filter(arr => arr.length > 0);

  if (attrArrays.length === 0) return [];

  // Cartesian product generates all attribute combinations
  const isSingle = attrArrays.length === 1;
  const combinations = cartesianProduct(attrArrays);

  return combinations.map((combo, index) => {
    const key = buildComboKey(combo, isSingle);

    return {
      id: index,
      title: combo.map(c => c.value).join(' | '),
      type: buildTypeMap(combo),
      images: variantImages[key],
      price: toNumber(price?.[key]),
      quantity: toNumber(quantity?.[key]),
      discount: toNumber(discount?.[key]),
      is_default: isDefault === key,
    };
  });
}

// ─── Main: AttributeValue[] → TableRow[] ─────────────────────────────────────

/**
 * Convert flat AttributeValue[] → TableRow[] for VariantTable display.
 * Generate all combinations via Cartesian product of attribute groups.
 *
 * Key convention: index-based "idx1-idx2-..." (not server IDs).
 *   Single attr: "0", "1", "2", ...
 *   Multi attr:  "0-0", "0-1", "1-0", "1-1", ...
 *
 * Qty/price/discount: "first-non-zero" merge from all attrs in combination.
 * (Assumes last attr is most specific; adjust merge logic per business needs.)
 *
 * @param {AttributeValue[]} attributeValues
 * @returns {TableRow[]}
 */
// export function toTableRows(attributeValues = []) {
//   if (attributeValues.length === 0) return [];

//   // Group by attribute name (preserves insertion order)
//   const grouped = groupBy(attributeValues, item => item.value);
//   const groups = Array.from(grouped.entries());

//   // Convert each group to indexed entries: { item, typeIdx, valueIdx }
//   const entryArrays = groups.map(([, items], typeIdx) =>
//     items.map((item, valueIdx) => ({ item, typeIdx, valueIdx }))
//   );

//   const valid = entryArrays.filter(arr => arr.length > 0);
//   if (valid.length === 0) return [];

//   // Cartesian product generates all combinations
//   const isSingle = valid.length === 1;
//   const combinations = cartesianProduct(valid);

//   return combinations.map((entries, comboIdx) => {
//     // Build key from value indices (e.g. "0" or "0-1")
//     const key = isSingle
//       ? String(entries[0].valueIdx)
//       : entries.map(e => e.valueIdx).join('-');

//     // Merge: take first non-zero value from combination attributes
//     const qty = entries.reduce((v, e) => v || e.item.quantity, 0);
//     const price = entries.reduce((v, e) => v || e.item.after_price, 0);
//     const discount = entries.reduce((v, e) => v || e.item.discount, 0);

//     return {
//       id: key,
//       attrs: entries.map(e => e.item),

//       // First attribute (for rowspan grouping)
//       attr1: entries[0]?.item.title ?? '',
//       attr1Sub: entries[0]?.item.value ?? '',
//       imageUrl: entries[0]?.item.icon ?? null,

//       // Second attribute (if exists)
//       attr2: entries[1]?.item.title ?? '',
//       attr2Sub: entries[1]?.item.value ?? '',

//       qty,
//       price,
//       discount,
//       isDefault: comboIdx === 0,
//     };
//   });
// }

// ─── groupRowSpans ────────────────────────────────────────────────────────────

/**
 * Calculate rowSpan per row for Ant Design Table vertical merge.
 * Groups rows by attr1 (first attribute value) and merges consecutive.
 * Returns map: row.id → rowSpan (non-first rows in group get 0).
 *
 * @param {TableRow[]} rows
 * @returns {Record<string, number>}
 */
// export function groupRowSpans(rows) {
//   if (rows.length === 0) return {};

//   // Count rows per group (attr1) and track first row in each group
//   const count = new Map();
//   const first = new Map();

//   rows.forEach(row => {
//     const groupKey = row.attr1 || row.id;
//     if (!count.has(groupKey)) {
//       count.set(groupKey, 0);
//       first.set(groupKey, row.id);
//     }
//     count.set(groupKey, count.get(groupKey) + 1);
//   });

//   // Return map: first row of group → count, others → 0
//   return Object.fromEntries(
//     rows.map(row => {
//       const groupKey = row.attr1 || row.id;
//       return [row.id, first.get(groupKey) === row.id ? count.get(groupKey) : 0];
//     })
//   );
// }

// ─── Reverse: VariantCombination[] → Form State ────────────────────────────────

/**
 * Reverse conversion: VariantCombination[] (with metadata) → form state.
 * Extracts attrId, valueId, attrName, price, qty, discount from combination's type.
 *
 * Input: Combinations where each variant[n] has full metadata:
 *   { attrId, valueId, attrName, value, icon, images }
 *
 * Output: Form state with keys matching single/multi attr conventions:
 *   Single attr: key = "valueId"       (e.g. "1", "2")
 *   Multi attr:  key = "v1Id-v2Id-..." (e.g. "1-1", "1-2")
 *
 * @param {Array<{ type: Record<string, { attrId, valueId, attrName, value, icon, images }>,
 *                  price, quantity, discount, is_default }>} combinations
 * @returns {Object} Form state { variantNames, variantValues, variantIcons, variantImages, quantity, price, discount, isDefault }
 *
 * @example
 * // Input: [
 * //   { type: { variant1: { attrId: "1", valueId: "1", value: "Đỏ", ... },
 * //             variant2: { attrId: "2", valueId: "1", value: "S", ... } },
 * //     price: 200000, quantity: 10, discount: 0, is_default: true }
 * // ]
 * // Output: { variantNames: { "1": "Màu", "2": "Size" },
 * //           variantValues: { "1": { "1": "Đỏ" }, "2": { "1": "S" } },
 * //           quantity: { "1-1": "10" },
 * //           ... }
 */
export function toFormState(combinations = []) {
  if (!Array.isArray(combinations) || combinations.length === 0) {
    return {
      variantNames: {},
      variantValues: {},
      variantIcons: {},
      variantImages: {},
      quantity: {},
      price: {},
      discount: {},
      isDefault: null,
    };
  }

  const variantNames = {};
  const variantValues = {};
  const variantIcons = {};
  const variantImages = {};
  const quantity = {};
  const price = {};
  const discount = {};
  let isDefault = null;

  combinations.forEach((combo, idx) => {
    const {
      type = {},
      price: comboPrice = 0,
      quantity: comboQty = 0,
      discount: comboDiscount = 0,
      images,
      is_default,
    } = combo;

    // Extract metadata from type: variant1, variant2, ...
    const variants = Object.values(type);

    // Build composite key from valueIds (single: "1", multi: "1-2")
    const valueIds = variants.map(v => String(v?.valueId ?? ''));
    const formKey = valueIds.length === 1 ? valueIds[0] : valueIds.join('-');

    // Collect variantNames, variantValues, variantIcons, variantImages
    variants.forEach((variant, pos) => {
      if (!variant) return;

      const { attrId, valueId, attrName, value, icon } = variant;

      // variantNames: attrId → attrName
      variantNames[attrId] = attrName;

      // variantValues: attrId → (valueId → value/title)
      if (!variantValues[attrId]) {
        variantValues[attrId] = {};
      }
      variantValues[attrId][valueId] = value;

      // variantIcons: attrId → (valueId → [icon])
      if (icon) {
        if (!variantIcons[attrId]) {
          variantIcons[attrId] = {};
        }
        variantIcons[attrId][valueId] = [icon];
      }

      // variantImages: "${attrId}-${valueId}" → [images]
      if (Array.isArray(images) && images.length > 0) {
        variantImages[formKey] = images;
      }
    });

    // Store qty/price/discount per formKey
    quantity[formKey] = String(comboQty);
    price[formKey] = String(comboPrice);
    discount[formKey] = comboDiscount ?? 0;

    // Track default variant
    if (is_default) {
      isDefault = formKey;
    }
  });

  return {
    variantNames,
    variantValues,
    variantIcons,
    variantImages,
    quantity,
    price,
    discount,
    isDefault,
  };
}

// ─── Reverse: TableRow[] → AttributeValue[] ──────────────────────────────────

// /**
//  * Reverse: sync TableRow edits (qty/price/discount) back to AttributeValue[].
//  * Strategy: each attribute value gets values from first combination containing it.
//  * (Assumes quantity/price per combination overrides attribute-level defaults.)
//  *
//  * @param {TableRow[]}       rows           - Rows after user edit
//  * @param {AttributeValue[]} originalValues - Original structure (preserves value/title/icon)
//  * @returns {AttributeValue[]}
//  */
// export function toAttributeValues(rows, originalValues) {
//   // Map each attribute to its values from first combination containing it
//   const seen = new Map();

//   rows.forEach(row => {
//     row.attrs.forEach(attr => {
//       const key = `${attr.value}|${attr.title}`;
//       if (!seen.has(key)) {
//         seen.set(key, { qty: row.qty, price: row.price, discount: row.discount });
//       }
//     });
//   });

//   // Apply edited values back to original structure
//   return originalValues.map(av => {
//     const key = `${av.value}|${av.title}`;
//     const data = seen.get(key);
//     return data
//       ? {
//           ...av,
//           quantity: data.qty,
//           after_price: data.price,
//           discount: data.discount,
//         }
//       : av;
//   });
// }
